import { useMemo } from 'react'
import { collection, query } from 'firebase/firestore'
import { db } from '../libs/Firebase'
import { PrivateLessonBlock } from '../../../pure-js/types/GrooverTypes'
import { Collections, Subcollections } from '../../../pure-js/libs/Collections'
import { useFirestoreQuery } from './useFirestoreQuery'
import { ReducerState } from './useFirestoreQueryHelper'

/**
 * Hook to fetch private lesson blocks for a client
 * Used in booking pages to grey out blocked time slots
 *
 * @param clientId - The client ID to fetch blocks for
 * @returns Query result with blocks data, loading state, and error
 */
export const usePrivateLessonBlocks = (clientId: string | null): ReducerState<PrivateLessonBlock[]> => {
  const firestoreQuery = useMemo(() => {
    if (!clientId) {
      // Return a dummy query when clientId is not available
      return query(collection(db, `${Collections.CLIENTS}/dummy/${Subcollections.PRIVATE_LESSON_BLOCKS}`))
    }
    return query(collection(db, `${Collections.CLIENTS}/${clientId}/${Subcollections.PRIVATE_LESSON_BLOCKS}`))
  }, [clientId])

  const result = useFirestoreQuery<PrivateLessonBlock[]>(firestoreQuery, {
    fnName: 'usePrivateLessonBlocks',
    enabled: !!clientId // Only run query when clientId is available
  })

  if (!clientId) {
    return {
      status: 'idle',
      data: undefined,
      error: new Error('clientId is not set'),
      isLoading: false
    }
  }

  return result
}
